import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { Building2, Check, Plus, Settings2 } from 'lucide-react'

import { CreateOrganizationDialog } from '@/components/CreateOrganizationDialog'
import { useOrgSwitch } from '@/components/OrgSwitcher'
import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'

/**
 * The organization part of the Site account menu (Plotea has no sidebar
 * switcher): the contexts to switch to, „Utwórz organizację” and the
 * organization page. Returns the create dialog separately, since a menu
 * item's dialog must outlive the closing menu.
 */
export function useAccountOrgMenu() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const org = useOrgSwitch()
  const items = (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuGroup data-testid="account-org-menu">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Organizacja
        </DropdownMenuLabel>
        {org.personalAvailable && (
          <DropdownMenuItem
            disabled={!org.canSwitchToPersonal || org.busy}
            onClick={org.switchToPersonal}
          >
            <Check className={org.activeOrg ? 'opacity-0' : undefined} />
            {t('organizations.personal')}
          </DropdownMenuItem>
        )}
        {org.orgs.map((item) => {
          const current = org.activeOrg?.id === item.id
          return (
            <DropdownMenuItem
              key={item.id}
              disabled={org.busy}
              aria-current={current || undefined}
              onClick={() => !current && org.switchTo(item.id)}
            >
              <Check className={current ? undefined : 'opacity-0'} />
              <span className="truncate">{item.name}</span>
            </DropdownMenuItem>
          )
        })}
        {org.canCreate && (
          <DropdownMenuItem onClick={() => setCreating(true)}>
            <Plus />
            {t('organizations.create.button')}
          </DropdownMenuItem>
        )}
        {org.orgs.length > 0 && (
          <DropdownMenuItem
            onClick={() => void navigate({ to: org.managePath })}
          >
            <Settings2 />
            Ustawienia organizacji
          </DropdownMenuItem>
        )}
      </DropdownMenuGroup>
    </>
  )
  const dialog = org.canCreate ? (
    <CreateOrganizationDialog open={creating} onOpenChange={setCreating} />
  ) : null
  return { label: org.label, active: Boolean(org.activeOrg), items, dialog }
}

/** The active organization as a quiet crumb before the analysis path. */
export function OrgCrumb({ label }: { label: string }) {
  return (
    <span className="site-top-bar-org" data-testid="account-org-crumb">
      <Building2 aria-hidden />
      <span className="truncate">{label}</span>
    </span>
  )
}
